import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const GOOGLE_FONTS = ["Barlow", "Barlow Condensed", "Barlow Semi Condensed", "Oswald", "Bebas Neue", "Anton", "Teko", "Rajdhani", "Saira", "Saira Condensed",
  "Saira Extra Condensed", "Exo 2", "Orbitron", "Chakra Petch", "Russo One", "Black Ops One", "Bungee", "Archivo", "Archivo Narrow", "Archivo Black", "Big Shoulders Display",
  "Kanit", "Titillium Web", "Sora", "Space Grotesk", "Outfit", "Manrope", "Plus Jakarta Sans", "DM Sans", "Inter", "Inter Tight", "Poppins", "Montserrat", "Raleway",
  "Roboto", "Roboto Condensed", "Roboto Slab", "Roboto Mono", "Open Sans", "Lato", "Nunito", "Nunito Sans", "Work Sans", "Rubik", "Mulish", "Urbanist", "Lexend",
  "Figtree", "Onest", "Red Hat Display", "IBM Plex Sans", "IBM Plex Sans Condensed", "Source Sans 3", "Fira Sans", "Fira Sans Condensed", "PT Sans", "PT Sans Narrow",
  "Ubuntu", "Josefin Sans", "Quicksand", "Cabin", "Cabin Condensed", "Karla", "Libre Franklin", "Public Sans", "Encode Sans", "Encode Sans Condensed", "Sofia Sans",
  "Sofia Sans Condensed", "Sofia Sans Extra Condensed", "Antonio", "Fjalla One", "League Gothic", "League Spartan", "Staatliches", "Alfa Slab One", "Passion One",
  "Squada One", "Racing Sans One", "Graduate", "Audiowide", "Michroma", "Syncopate", "Monoton", "Bowlby One", "Bowlby One SC", "Changa", "Changa One", "Khand",
  "Hind", "Hind Madurai", "Mukta", "Signika", "Asap", "Asap Condensed", "Barlow", "Jost", "Epilogue", "Bricolage Grotesque", "Schibsted Grotesk", "Instrument Sans",
  "Hanken Grotesk", "Albert Sans", "Be Vietnam Pro", "Playfair Display", "Merriweather", "Lora", "Libre Baskerville", "DM Serif Display", "Abril Fatface",
  "Permanent Marker", "Rock Salt", "Caveat", "Pacifico", "Lobster", "Righteous", "Press Start 2P", "VT323", "Silkscreen", "Space Mono", "JetBrains Mono"].filter((f, i, a) => a.indexOf(f) === i);

export const COLOR_KEYS = [["bg", "Fond de page"], ["surface", "Fond des cartes"], ["border", "Bordures"], ["header_bg", "Fond de l'en-tête et du menu"],
  ["text", "Texte principal"], ["muted", "Texte secondaire"], ["title", "Titres"], ["eyebrow", "Sur-titres (rubriques)"], ["link", "Liens"],
  ["label", "\u00c9tiquettes et pastilles"], ["accent", "Accent (mots en couleur)"], ["button_bg", "Fond des boutons"], ["button_text", "Texte des boutons"]];

let current = null;
const subs = new Set();
const loaded = new Set();

export function ensureFont(f) {
  if (!f || loaded.has(f)) return;
  loaded.add(f);
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f).replace(/%20/g, "+")}:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,700;1,800&display=swap`;
  document.head.appendChild(l);
}

const fam = (f) => `"${f}", sans-serif`;
const onColor = (hex) => {
  if (!hex) return "";
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return 1.05 / (L + 0.05) >= (L + 0.05) / 0.05 ? "#FFFFFF" : "#080C0D";
};
const rule = (sel, decl) => { const d = Object.entries(decl).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}: ${v} !important;`).join(" "); return d ? `${sel} { ${d} }\n` : ""; };

function scopeCss(s, site) {
  if (!s) return "";
  [s.title_font, s.body_font, s.button_font].forEach(ensureFont);
  const P = site ? "html body .pub" : "html body:not(:has(.pub))";
  const btn = site ? ":is(.p-btn, .p-btn-ghost, button)" : ":is(.mpl-btn-primary, .mpl-btn-ghost, button)";
  const card = site ? ":is(.p-surface, .p-raised)" : ":is(.mpl-card)";
  let css = "";
  css += rule(site ? "html:has(.pub)" : "html:not(:has(.pub))", { "font-size": s.base_size ? `${s.base_size}px` : "" });
  css += rule(P, { "font-family": s.body_font && fam(s.body_font) });
  css += rule(`${P} :is(h1, h2, h3, .font-display)`, { "font-family": s.title_font && fam(s.title_font), "font-weight": s.title_weight || "", "font-style": s.title_style,
    "text-transform": s.title_case, "letter-spacing": s.title_spacing ? `${s.title_spacing}em` : "" });
  css += rule(`${P} :is(h1, h2)`, { zoom: s.title_scale ? s.title_scale / 100 : "" });
  css += rule(`${P} :is(.mpl-eyebrow, .p-eyebrow)`, { "font-size": s.eyebrow_size ? `${s.eyebrow_size}px` : "" });
  css += rule(`${P} ${btn}`, { "font-family": s.button_font && fam(s.button_font), "font-size": s.button_size ? `${s.button_size}px` : "", "font-weight": s.button_weight || "",
    "border-radius": s.button_radius >= 0 ? `${s.button_radius}px` : "" });
  css += rule(`${P} ${card}`, { "border-radius": s.card_radius >= 0 ? `${s.card_radius}px` : "", "border-width": s.border_width >= 0 ? `${s.border_width}px` : "" });
  ["day", "night"].forEach((m) => {
    const c = s[m] || {};
    const M = site ? `html[data-mode="${m}"] body .pub` : `html[data-mode="${m}"]:not(.mpl-branded) body:not(:has(.pub)):not(:has(.mpl-branded))`;
    css += rule(M, site ? { "--p-bg": c.bg, "--p-bg-2": c.bg, "--p-surface": c.surface, "--p-raised": c.surface, "--p-line": c.border, "--p-text": c.text, "--p-text-2": c.muted, "--p-text-3": c.muted, "--p-accent": c.accent, "background-color": c.bg, color: c.text }
      : { "--mpl-night": c.bg, "--mpl-deep": c.bg, "--mpl-card": c.surface, "--mpl-line": c.border, "--mpl-text": c.text, "background-color": c.bg, color: c.text });
    css += rule(`${M} :is(main, [class*="bg-[#05070D]"], [class*="bg-[#0B1020]"])`, { "background-color": c.bg });
    css += rule(`${M} ${card}`, { "background-color": c.surface, "border-color": c.border });
    css += rule(`${M} [class*="border-white/"]`, { "border-color": c.border });
    css += rule(`${M} :is(header, aside, nav[class*="fixed"])`, { "background-color": c.header_bg });
    css += rule(`${M} :is(.text-white, .text-slate-100, .text-slate-200, .p-t1)`, { color: c.text });
    css += rule(`${M} :is(.text-slate-300, .text-slate-400, .text-slate-500, .text-slate-600, .p-t2, .p-t3)`, { color: c.muted });
    css += rule(`${M} :is(h1, h2, h3, .font-display)`, { color: c.title });
    css += rule(`${M} :is(.mpl-eyebrow, .p-eyebrow)`, { color: c.eyebrow });
    css += rule(`${M} :is(a.underline, a[class*="hover:underline"], .mpl-link, .p-link)`, { color: c.link });
    css += rule(`${M} :is(.p-chip, .mpl-chip, [class*="rounded-full"][class*="text-[10px]"], [class*="rounded-full"][class*="text-[11px]"])`, { color: c.label, "border-color": c.label });
    css += rule(`${M} :is([class~="text-[#B7F52A]"], .p-accent, .text-lime-300)`, { color: c.accent });
    css += rule(`${M} :is([class*="bg-[#B7F52A]/"], [class*="border-[#B7F52A]"])`, { "border-color": c.accent });
    css += rule(`${M} :is(.mpl-btn-primary, [class~="bg-[#B7F52A]"], .p-btn)`, { "background-color": c.button_bg, "border-color": c.button_bg, color: c.button_text || onColor(c.button_bg) });
  });
  return css;
}

export function applyAppearance(a) {
  current = a;
  let el = document.getElementById("mpl-appearance");
  if (!el) { el = document.createElement("style"); el.id = "mpl-appearance"; document.head.appendChild(el); }
  el.textContent = a ? scopeCss(a.game, false) + scopeCss(a.site, true) : "";
  subs.forEach((f) => f(a));
}

export function initAppearance() {
  api.get("/site/appearance").then(({ data }) => applyAppearance(data)).catch(() => {});
}

export const assetUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : "");

export function useAppearance(scope) {
  const [a, setA] = useState(current);
  useEffect(() => { subs.add(setA); return () => { subs.delete(setA); }; }, []);
  return a?.[scope] || {};
}
